import React, { useContext, useEffect, useState } from 'react';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Alert } from '@one-thd/sui-atomic-components';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { CustomerAccountNavigation, useAccountCrumbs, } from '@thd-olt-component-react/customer-account-navigation';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ProfileProvider } from '../contexts/profile-context';
import { SecurityAlert } from '../components/security-alert/security-alert';
import { PageTitle } from '../components/shared/page-title/page-title';
import { UpgradeToPro } from '../components/upgrade-to-pro/upgrade-to-pro';
import { ERR_GENERIC } from '../constants/response-error-messages';
import { useInterceptors } from '../hooks/use-interceptors';
import { PersonalInfo } from '../components/personal-info/personal-info';
import { TaxExempt } from '../components/tax-exempt/tax-exempt';
import { MyStore } from '../components/my-store/my-store';
import { CompanyDetails } from '../components/company-details/company-details';
import Spinner from '../components/shared/spinner/spinner';
import { useProfile } from '../hooks/use-profile';
import { setAnalytics, setPageErrorInfo } from '../utils/analytics';
import { getConfigurationValue, getNativeStoreID } from '../utils/general';
import { b2bAccountApi } from '../api/b2b/account';
import { getB2BUpdateProfilePreferenceRequest, getB2CUpdateProfileRequestData, } from '../utils/form';
import { getLocalizedStoreId } from '../utils/store';
import { AccountClosure } from '../components/account-closure/account-closure';
import { newrelic } from '../utils/newrelic';
import { mapCrumbsToStaticCrumbs, } from '../utils/breadcrumbs';
var crumbs = [
    {
        label: 'Profile',
        path: '/myaccount/profile',
    },
];
var ProfilePageContent = function () {
    var executeRecaptcha = useGoogleReCaptcha().executeRecaptcha;
    var _a = useState(null), generalError = _a[0], setGeneralError = _a[1];
    var isConsumerApp = useContext(ExperienceContext).isConsumerApp;
    var redirectError = useInterceptors().redirectError;
    var _b = useProfile(), getProfileData = _b.getProfileData, customerData = _b.customerData, updatePersonalDetails = _b.updatePersonalDetails, isB2B = _b.isB2B;
    var _c = useState(true), isLoading = _c[0], setLoading = _c[1];
    var showOnApp = getConfigurationValue('ENABLE_CTP_WEBVIEW') === 'true';
    var shouldShowCtp = !isB2B && (!isConsumerApp || showOnApp);
    var staticCrumbs = mapCrumbsToStaticCrumbs(useAccountCrumbs(crumbs));
    var updateStoreId = function (storeId, data) {
        var _a, _b;
        if (isB2B) {
            var request = getB2BUpdateProfilePreferenceRequest(storeId);
            b2bAccountApi.updateEmailPreference(request);
        }
        else {
            var request = getB2CUpdateProfileRequestData(data, {
                localStoreId: storeId,
            });
            newrelic.addPageAction('zip-code-from-updateStoreId', {
                zipcode: (_b = (_a = request === null || request === void 0 ? void 0 : request.account) === null || _a === void 0 ? void 0 : _a.profile) === null || _b === void 0 ? void 0 : _b.zipCode,
            });
            updatePersonalDetails(request);
        }
    };
    useEffect(function () {
        // to avoid the recaptcha race condition
        if (!customerData || !executeRecaptcha) {
            return;
        }
        var storeId = getNativeStoreID() || getLocalizedStoreId();
        var shouldUpdateStoreId = !!storeId && Number(storeId) !== Number(customerData.localStoreId);
        if (shouldUpdateStoreId) {
            updateStoreId(storeId, customerData.response);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerData, executeRecaptcha]);
    useEffect(function () {
        setLoading(true);
        getProfileData()
            .catch(function () {
            setGeneralError(ERR_GENERIC);
            setPageErrorInfo('error', 'page', 'my account page', ERR_GENERIC.message, 'n/a');
        })
            .finally(function () {
            setLoading(false);
        });
    }, []);
    useEffect(function () {
        var page = {
            category: {
                primaryCategory: 'account',
                subCategory1: 'account>profile',
                subCategory2: 'account>profile',
            },
            pageInfo: {
                pageName: 'account>profile',
                pageType: 'maml:profile',
            },
            error: [],
        };
        if (window.digitalData) {
            window.digitalData.page = page;
            setAnalytics(window.digitalData);
        }
    }, []);
    return (React.createElement("div", { className: "grid isBound" },
        React.createElement("div", { className: "col__12-12" },
            React.createElement(Spinner, { active: isLoading }),
            redirectError ? (React.createElement("div", { className: "sui-pt-8" },
                React.createElement(Alert, { status: "info" }, redirectError.message))) : (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "sui-grid sui-gap-6 sm:sui-gap-3 sui-py-3" },
                    !isConsumerApp && (React.createElement(ErrorBoundary, { name: "breadcrumbs" },
                        React.createElement(Breadcrumbs, { staticCrumbs: staticCrumbs }))),
                    React.createElement(PageTitle, { title: "Profile" })),
                React.createElement("div", { className: "sui-grid sui-grid-cols-12 sui-gap-4 sm:sui-py-3" },
                    React.createElement("div", { className: "sui-col-span-12 lg:sui-col-span-3" },
                        React.createElement(ErrorBoundary, { name: "customer-account-navigation" },
                            React.createElement(CustomerAccountNavigation, null))),
                    React.createElement("div", { className: "sui-col-span-12 lg:sui-col-span-9" }, generalError ? (React.createElement(Alert, { status: "error" }, generalError.message)) : (React.createElement(React.Fragment, null,
                        React.createElement(ErrorBoundary, { name: "security-alert" },
                            React.createElement(SecurityAlert, null)),
                        !!customerData && (React.createElement("div", { className: "sui-grid sui-gap-x-4 sui-gap-y-12 sui-grid-cols-1 lg:sui-grid-cols-2" },
                            React.createElement(ErrorBoundary, { name: "personal-info" },
                                React.createElement(PersonalInfo, null)),
                            shouldShowCtp && (React.createElement("div", { className: "sui-col-start-1 lg:sui-col-start-2 lg:sui-row-span-2" },
                                React.createElement(ErrorBoundary, { name: "upgrade-to-pro" },
                                    React.createElement(UpgradeToPro, null)))),
                            customerData.companyDetails && (React.createElement("div", { className: "sui-col-start-1" },
                                React.createElement(ErrorBoundary, { name: "company-details" },
                                    React.createElement(CompanyDetails, null)))),
                            React.createElement("div", { className: "sui-col-start-1" },
                                React.createElement(ErrorBoundary, { name: "tax-exempt-or-my-store" }, isB2B ? React.createElement(TaxExempt, null) : React.createElement(MyStore, null))),
                            React.createElement("div", { className: "sui-col-start-1" },
                                React.createElement(ErrorBoundary, { name: "my-store-or-tax-exempt" }, isB2B ? React.createElement(MyStore, null) : React.createElement(TaxExempt, null))),
                            !isB2B && !isConsumerApp && (React.createElement("div", { className: "sui-col-start-1" },
                                React.createElement(ErrorBoundary, { name: "my-store-or-tax-exempt" },
                                    React.createElement(AccountClosure, null)))))))))))))));
};
export var ProfilePage = function (_a) {
    var customerType = _a.customerType;
    var isB2B = customerType.toUpperCase() === 'B2B';
    return (React.createElement(ProfileProvider, { isB2B: isB2B },
        React.createElement(QueryProvider, { cacheKey: "profile-page" },
            React.createElement(ProfilePageContent, null))));
};
ProfilePageContent.displayName = 'ProfilePageContent';
ProfilePageContent.dataModel = extend(CustomerAccountNavigation, Breadcrumbs);
ProfilePage.displayName = 'ProfilePage';
